import { Container, IInit } from 'common/container/Container'
import { ConnectableObservable, Observable, Subject } from 'rxjs'
import { multicast } from 'rxjs/operators'
import { emptyLoggedUserDTO, LoggedUserDTO, toModel } from '../models/LoggedUserDTO'
import { LoggedUser } from '../models/LoggedUser'
import { AuthDTO } from '../models/AuthDTO'
import { IStatusService } from '../../../common/status/StatusService'
import { IAuthApi } from '../api/AuthApi'
import { AUTH_SERVICE_KEY } from '../container'
import { RegisterDTO } from '../../users/models/RegisterDTO'
import { ChangePasswordDTO } from '../models/ChangePassword'
import { Permission } from '../../../common/enums/Permissions'
import { Error } from '../../users/models/Error'

export interface IAuthService extends IInit {
  login(a: AuthDTO): Observable<LoggedUserDTO | Error>

  logout(): void

  getObservable(): Observable<LoggedUser>

  get(): LoggedUser

  register(r: RegisterDTO): Observable<boolean>

  userCan(perm: string): boolean

  changePassword(a: ChangePasswordDTO): Observable<boolean>

  changePasswordWithToken(a: ChangePasswordDTO, token: string): Observable<boolean>

  recoverPassword(a: { email: string }): Observable<boolean>

  update(): Observable<LoggedUserDTO | undefined>

  switchState(id: string): Observable<boolean>
}

const LOGGED_USER_KEY = 'logged user'

type Props = {
  apiKey: symbol
}

export class AuthService implements IAuthService {
  private readonly _apiKey: symbol
  private _container!: Container
  private _api!: IAuthApi
  private _statusService!: IStatusService

  private _user: LoggedUser = toModel(emptyLoggedUserDTO())
  private _userSubject = new Subject<LoggedUser>()
  // [FIX] timeout almacena el handle del timer de expiración de sesión.
  // Se usa para cancelarlo con clearTimeout antes de crear uno nuevo,
  // evitando la acumulación de timers (memory leak).
  private timeout: NodeJS.Timeout | null = null;
  // [FIX] inactivityTimeout almacena el handle del timer de inactividad.
  // Se reinicia con cada evento de actividad del usuario.
  private inactivityTimeout: NodeJS.Timeout | null = null;
  private inactivityTime = 10800000; // 3 horas en ms
  private _isActive = true;

  constructor(p: Props) {
    this.loadUser()
    this._apiKey = p.apiKey
    // [NEW] Registra listeners globales de actividad del usuario.
    // Cada evento resetea _isActive a true y reinicia el timer de inactividad.
    // Se usan funciones con nombre para poder quitarlas con removeEventListener
    // si la instancia necesita destruirse (evita leak en tests/HMR).
    this._handleUserActivity = this._handleUserActivity.bind(this)
    const events = ['mousemove', 'keydown', 'click', 'scroll']
    events.forEach((evt) =>
      window.addEventListener(evt, this._handleUserActivity, { passive: true })
    )
    this._resetInactivityTimer()
  }

  init(c: Container) {
    this._container = c
    this._api = this._container.get<IAuthApi>(this._apiKey)
    this._statusService = this._container.get<IStatusService>(AUTH_SERVICE_KEY)
  }

  login(a: AuthDTO): Observable<LoggedUserDTO | Error> {
    const multi = this._api
      .login(a)
      .pipe(multicast(() => new Subject<LoggedUserDTO | Error>())) as ConnectableObservable<
        LoggedUserDTO | Error
      >

    multi.connect()
    multi.subscribe((dto) => {
      if (Object.keys(dto).every((k) => k !== "message")) {
        this.storeUser(dto as LoggedUserDTO)
      }
    })
    return multi
  }

  logout() {
    // [FIX] Cancela el timer de expiración pendiente antes de hacer logout
    // para que no dispare un segundo ciclo tras la limpieza de sesión.
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
    this._api.logout().subscribe(() => {
      this.removeUser()
    })
  }

  // [FIX] logoutExpired ahora es el punto de entrada desde loadUser/storeUser.
  // Comprueba _isActive (actualizado por _handleUserActivity) y decide si
  // renovar la sesión o hacer logout definitivo.
  logoutExpired() {
    if (this._isActive) {
      this.extendSession();
    } else {
      if (this.timeout) {
        clearTimeout(this.timeout)
        this.timeout = null
      }
      this._api.logout().subscribe(() => {
        this.removeUser();
      });
    }
  }

  // [FIX] extendSession llama al backend (PUT /extend-session) para renovar
  // la cookie HttpOnly y obtener el sessionExpires real del servidor.
  // Antes solo parcheaba localStorage, lo que dejaba la cookie caducada
  // y causaba que todos los requests fallasen tras el tiempo original.
  // El timer anterior siempre se cancela antes de crear uno nuevo (sin leak).
  private extendSession() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
    this._api.extendSession().subscribe((dto) => {
      if (dto) {
        // Usa la fecha que devuelve el servidor como fuente de verdad.
        this.storeUser(dto)
      } else {
        // Si el backend rechaza la renovación (sesión ya inválida), hacemos logout.
        this.removeUser()
      }
    })
  }

  // [NEW] Handler de actividad: marca _isActive = true y reinicia el timer
  // de inactividad. Es privado y su referencia se almacena en la instancia
  // para poder enlazarlo/desenlazarlo como listener.
  private _handleUserActivity() {
    this._isActive = true
    this._resetInactivityTimer()
  }

  // [NEW] Reinicia el timer de inactividad. Si pasan inactivityTime (20 min)
  // sin ningún evento de actividad, _isActive se pone a false. La próxima
  // vez que logoutExpired() se ejecute, disparará el logout definitivo.
  private _resetInactivityTimer() {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout)
    }
    this.inactivityTimeout = setTimeout(() => {
      this._isActive = false
    }, this.inactivityTime)
  }

  register(r: RegisterDTO): Observable<boolean> {
    return this._api.register(r)
  }

  get(): LoggedUser {
    return this._user
  }

  updateLocalProfile(l: LoggedUserDTO): void {
    localStorage.setItem(LOGGED_USER_KEY, JSON.stringify(l))
    this._user = toModel(l)
    this.next()
  }

  getObservable(): Observable<LoggedUser> {
    return this._userSubject.pipe()
  }

  userCan(perm: Permission): boolean {
    return this._user?.permissions?.includes(perm)
  }

  private next() {
    this._userSubject.next(this._user)
  }

  private loadUser() {
    const dto = (JSON.parse(localStorage.getItem(LOGGED_USER_KEY) || 'null') ||
      undefined) as LoggedUserDTO
    if (dto && new Date(dto.sessionExpires) > new Date()) {
      this._user = toModel(dto)
      this.next()
      // [FIX] Cancela cualquier timer previo y usa logoutExpired en lugar de
      // logout directo, activando el ciclo de renovación por actividad.
      if (this.timeout) clearTimeout(this.timeout)
      this.timeout = setTimeout(
        () => this.logoutExpired(),
        this._user.sessionExpires.valueOf() - new Date().valueOf()
      )
    }
  }

  private storeUser(dto: LoggedUserDTO) {
    localStorage.setItem(LOGGED_USER_KEY, JSON.stringify(dto))
    this._user = toModel(dto)
    this.next()
    // [FIX] Igual que loadUser: cancela el timer anterior y usa logoutExpired.
    if (this.timeout) clearTimeout(this.timeout)
    this.timeout = setTimeout(
      () => this.logoutExpired(),
      this._user.sessionExpires.valueOf() - new Date().valueOf()
    )
  }

  private removeUser() {
    localStorage.removeItem(LOGGED_USER_KEY)
    this._user = toModel(emptyLoggedUserDTO())
    this.next()
  }

  changePassword(a: ChangePasswordDTO): Observable<boolean> {
    return this._api.changePassword(a)
  }

  changePasswordWithToken(a: ChangePasswordDTO, token: string): Observable<boolean> {
    return this._api.changePasswordWithToken(a, token)
  }

  recoverPassword(a: { email: string }): Observable<boolean> {
    return this._api.recoverPassword(a)
  }

  update(): Observable<LoggedUserDTO | undefined> {
    const multi = this._api
      .update()
      .pipe(multicast(() => new Subject<LoggedUserDTO | undefined>())) as ConnectableObservable<
        LoggedUserDTO | undefined
      >

    multi.connect()
    multi.subscribe((dto) => {
      if (dto) {
        this.storeUser(dto)
      }
    })

    return multi
  }

  switchState(id: string): Observable<boolean> {
    return this._api.switchState(id)
  }
}
